import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Seo } from "../lib/seo";
import { fetchArticles } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { SectionHeading, ArticleCard } from "../components/bits";
import { Reveal } from "../components/motion";

export const CATEGORIES = [
  "Trademark & Brand Protection",
  "Contract Drafting Tips",
  "Corporate Legal Updates",
  "Property Due Diligence",
  "Regulatory Compliance",
  "Business Law in India",
];

export const Insights = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "All";
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["articles"],
    queryFn: () => fetchArticles(),
  });

  const filtered =
    active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <>
      <Seo
        title="Legal Resources & Insights"
        description="Practical legal insights for Indian businesses — trademark and brand protection, contract drafting, corporate legal updates, property due diligence, compliance and business law."
        path="/insights"
      />
      <PageHero
        eyebrow="Legal Resources & Insights"
        title={["Notes from the practice,", "written to be useful."]}
        description="Plain-language articles on the legal questions Indian businesses actually ask. Every piece is general information — not legal advice."
      />

      <section className="bg-white py-20 sm:py-24">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter by category">
              {["All", ...CATEGORIES].map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={active === c}
                  data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setParams(c === "All" ? {} : { category: c })}
                  className={`rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300 ${
                    active === c
                      ? "border-navy bg-navy text-gold"
                      : "border-line text-steel hover:border-gold hover:text-gold-dark"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          {isLoading ? (
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-72 animate-pulse border border-line bg-canvas" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="mt-16 text-center text-steel">
              No articles in this category yet — check back soon.
            </p>
          ) : (
            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a, i) => (
                <ArticleCard key={a.slug} article={a} index={i % 3} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Insights;
